import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Divider,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControlLabel,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Paper,
    Snackbar,
    Stack,
    Switch,
    Tab,
    Tabs,
    TextField,
    Typography
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { APIURL } from '../../../config';

const entityTypes = [
    { value: 'task_type', label: 'Task Types', singular: 'task type' },
    { value: 'employee', label: 'Employees', singular: 'employee' },
    { value: 'location', label: 'Locations', singular: 'location' }
];

const getLocationLabel = (location) => [
    location.loc_addr_line1,
    location.loc_addr_line2,
    location.loc_city,
    location.loc_state,
    location.loc_zip
].filter(Boolean).join(', ');

const getSelections = (associations, programId) => {
    const selected = { task_type: [], employee: [], location: [] };
    associations.filter(item => String(item.program_id) === String(programId)).forEach(item => {
        if (selected[item.entity_type] && !selected[item.entity_type].includes(String(item.entity_id))) {
            selected[item.entity_type].push(String(item.entity_id));
        }
    });
    return selected;
};

const notifyProgramChange = () => window.dispatchEvent(new Event('programs-updated'));

export default function Programs(props) {
    const manageProgramRef = useRef(null);
    const [programs, setPrograms] = useState([]);
    const [catalog, setCatalog] = useState([]);
    const [companyStateCodes, setCompanyStateCodes] = useState(new Set());
    const [programToEnroll, setProgramToEnroll] = useState(null);
    const [programToRemove, setProgramToRemove] = useState(null);
    const [programToDelete, setProgramToDelete] = useState(null);
    const [associations, setAssociations] = useState([]);
    const [options, setOptions] = useState({
        task_type: [],
        employee: [],
        location: []
    });
    const [selectedProgram, setSelectedProgram] = useState(null);
    const [selectedByType, setSelectedByType] = useState({
        task_type: [],
        employee: [],
        location: []
    });
    const [activeType, setActiveType] = useState('task_type');
    const [searchTerm, setSearchTerm] = useState('');
    const [newProgramName, setNewProgramName] = useState('');
    const [programNameError, setProgramNameError] = useState(false);
    const [programName, setProgramName] = useState('');
    const [programNameEditError, setProgramNameEditError] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [changed, setChanged] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');

    const loadProgramsData = useCallback(async (showLoading = true) => {
        if (showLoading) setLoading(true);
        setError('');

        try {
            const [programResponse, associationResponse, taskTypeResponse, employeeResponse, locationResponse, catalogResponse, stateResponse] = await Promise.all([
                axios.get(`${APIURL}/programs`, { params: { company_id: props.comp_id, emp_id: props.user?.emp_id } }),
                axios.get(`${APIURL}/programs/associations`, { params: { company_id: props.comp_id, emp_id: props.user?.emp_id } }),
                axios.get(`${APIURL}/dropdowns/dropdownType/taskTypeList`, { params: { comp_id: props.comp_id } }),
                axios.get(`${APIURL}/dropdowns/dropdownType/employeeslist`, { params: { comp_id: props.comp_id } }),
                axios.get(`${APIURL}/location`, { params: { comp_id: props.comp_id, program_id: '' } }),
                axios.get(`${APIURL}/programs/catalog`, { params: { com_id: props.comp_id, emp_id: props.user?.emp_id } }),
                axios.get(`${APIURL}/config/settings`, { params: { comp_id: props.comp_id, feature_id: 99 } })
            ]);

            const programList = Array.isArray(programResponse.data) ? programResponse.data : [];
            setPrograms(programList);
            setCatalog(Array.isArray(catalogResponse.data) ? catalogResponse.data : []);
            setCompanyStateCodes(new Set((Array.isArray(stateResponse.data) ? stateResponse.data : [])
                .map(state => String(state.state_id || '').trim().toUpperCase()).filter(Boolean)));
            setAssociations(Array.isArray(associationResponse.data) ? associationResponse.data : []);
            setOptions({
                task_type: (Array.isArray(taskTypeResponse.data) ? taskTypeResponse.data : []).map(item => ({
                    id: String(item.ddID),
                    label: item.EntityValue || `Task Type #${item.ddID}`
                })),
                employee: (Array.isArray(employeeResponse.data) ? employeeResponse.data : []).map(item => ({
                    id: String(item.id ?? item.emp_id),
                    label: item.employee_name ||
                        `${item.emp_first_name || ''} ${item.emp_last_name || ''}`.trim() ||
                        `Employee #${item.id ?? item.emp_id}`
                })),
                location: (Array.isArray(locationResponse.data) ? locationResponse.data : []).map(item => ({
                    id: String(item.loc_id),
                    label: getLocationLabel(item) || item.loc_name || `Location #${item.loc_id}`
                }))
            });

            setSelectedProgram(current => {
                if (!current) return null;
                return programList.find(program =>
                    String(program.program_id) === String(current.program_id)
                ) || null;
            });
        } catch (err) {
            console.error(err);
            setError(err.response?.data?.message || 'Unable to load program settings.');
        } finally {
            if (showLoading) setLoading(false);
        }
    }, [props.comp_id, props.user?.emp_id]);

    useEffect(() => {
        setSelectedProgram(null);
        setProgramToEnroll(null);
        setProgramToRemove(null);
        setProgramToDelete(null);
        setNewProgramName('');
        loadProgramsData();
    }, [loadProgramsData]);

    useEffect(() => {
        setSelectedByType(getSelections(associations, selectedProgram?.program_id));
        setChanged(false);
        setSearchTerm('');
    }, [associations, selectedProgram]);

    useEffect(() => {
        setProgramName(selectedProgram?.program_name || '');
        setProgramNameEditError(false);
    }, [selectedProgram]);

    const selectedIds = selectedByType[activeType];
    const filteredOptions = useMemo(() => {
        const query = searchTerm.trim().toLowerCase();
        return options[activeType].filter(option =>
            !query || option.label.toLowerCase().includes(query)
        );
    }, [activeType, options, searchTerm]);
    const availableOptions = filteredOptions.filter(option => !selectedIds.includes(option.id));
    const assignedOptions = selectedIds.map(id => options[activeType].find(option => option.id === id) || {
        id, label: `${entityTypes.find(type => type.value === activeType).singular} #${id} (unavailable)`
    });
    const activeTypeDetails = entityTypes.find(type => type.value === activeType);
    const canRename = selectedProgram?.definition_company_id != null &&
        String(selectedProgram.definition_company_id) === String(props.comp_id);
    const customPrograms = catalog.filter(program => program.company_id != null &&
        String(program.company_id) === String(props.comp_id));
    const isEnrolled = definition => programs.some(program =>
        program.medicaid_program_id.toLowerCase() === definition.medicaid_program_id.toLowerCase());
    const availablePrograms = useMemo(() => {
        const enrolled = new Set(programs.map(program => program.medicaid_program_id.toLowerCase()));
        return catalog.filter(program => program.is_active && !enrolled.has(program.medicaid_program_id.toLowerCase()) && (
            program.company_id != null
                ? String(program.company_id) === String(props.comp_id)
                : companyStateCodes.has(String(program.state_code || '').trim().toUpperCase())
        )).sort((a, b) => (
            Number(a.company_id != null) - Number(b.company_id != null) ||
            (a.state_code || '').localeCompare(b.state_code || '') ||
            a.program_name.localeCompare(b.program_name)
        ));
    }, [catalog, programs, companyStateCodes, props.comp_id]);

    const updateSelected = (updater) => {
        setSelectedByType(current => ({
            ...current,
            [activeType]: updater(current[activeType])
        }));
        setChanged(true);
    };

    const toggleOption = (option) => {
        updateSelected(current =>
            current.includes(option.id)
                ? current.filter(id => id !== option.id)
                : [...current, option.id]
        );
    };

    const handleCreateProgram = async () => {
        if (saving || changed) return;
        const programName = newProgramName.trim();
        if (!programName) {
            setProgramNameError(true);
            setMessage('');
            setError('A program name is required.');
            return;
        }

        setSaving(true);
        setMessage('');
        setError('');
        setProgramNameError(false);

        try {
            const response = await axios.post(`${APIURL}/programs`, {
                program_name: programName,
                company_id: props.comp_id,
                created_by: props.user?.emp_id,
                updated_by: props.user?.emp_id
            }, { params: { emp_id: props.user?.emp_id } });
            await loadProgramsData(false);
            setSelectedProgram(response.data);
            setNewProgramName('');
            notifyProgramChange();
            setMessage('Company program created and enrolled successfully.');
        } catch (err) {
            console.error(err);
            setError(err.response?.data?.message || 'Unable to create program.');
        } finally {
            setSaving(false);
        }
    };

    const handleRenameProgram = async () => {
        const nextProgramName = programName.trim();
        if (!canRename || saving || changed) return;

        if (!nextProgramName) {
            setProgramNameEditError(true);
            setMessage('');
            setError('A program name is required.');
            return;
        }

        if (nextProgramName === (selectedProgram.program_name || '').trim()) return;

        setSaving(true);
        setMessage('');
        setError('');
        setProgramNameEditError(false);

        try {
            await axios.put(`${APIURL}/programs`, {
                program_id: Number(selectedProgram.program_id),
                program_name: nextProgramName,
                company_id: props.comp_id,
                created_by: selectedProgram.created_by ?? null,
                updated_by: props.user?.emp_id
            }, { params: { emp_id: props.user?.emp_id } });

            await loadProgramsData(false);
            setSelectedProgram(current => current ? {
                ...current,
                program_name: nextProgramName
            } : current);
            setMessage('Program renamed successfully.');
            notifyProgramChange();
        } catch (err) {
            console.error(err);
            setError(err.response?.data?.message || 'Unable to rename program.');
        } finally {
            setSaving(false);
        }
    };

    const handleSaveAssociations = async () => {
        if (!selectedProgram || !changed || saving) return;

        setSaving(true);
        setMessage('');
        setError('');

        try {
            await axios.put(`${APIURL}/programs/${selectedProgram.program_id}/associations`, {
                company_id: props.comp_id,
                associations: entityTypes.flatMap(type => selectedByType[type.value].map(entityId => ({
                    entity_type: type.value, entity_id: entityId
                })))
            }, {
                params: { emp_id: props.user?.emp_id },
                headers: { 'Content-Type': 'application/json' }
            });

            await loadProgramsData(false);
            setChanged(false);
            setMessage('Program associations saved successfully.');
            notifyProgramChange();
        } catch (err) {
            console.error(err);
            setError(err.response?.data?.message || err.message || 'Unable to save program associations.');
        } finally {
            setSaving(false);
        }
    };

    const handleEnroll = async () => {
        if (!programToEnroll || saving || changed) return;
        setSaving(true);
        setMessage('');
        setError('');
        try {
            const response = await axios.post(`${APIURL}/programs/enrollments`, {
                com_id: props.comp_id, medicaid_program_id: programToEnroll.medicaid_program_id, is_supported: true
            }, { params: { emp_id: props.user?.emp_id } });
            await loadProgramsData(false);
            setSelectedProgram(response.data);
            setProgramToEnroll(null);
            setMessage('Program enrolled successfully.');
            notifyProgramChange();
        } catch (err) {
            setError(err.response?.data?.message || 'Unable to enroll program.');
        } finally { setSaving(false); }
    };

    const handleSupport = async (program, supported) => {
        if (saving || changed) return;
        setSaving(true);
        setMessage('');
        setError('');
        try {
            await axios.put(`${APIURL}/programs`, {
                program_id: program.program_id, company_id: props.comp_id, is_supported: supported
            }, { params: { emp_id: props.user?.emp_id } });
            await loadProgramsData(false);
            setMessage('Program support updated.');
            notifyProgramChange();
        } catch (err) {
            setError(err.response?.data?.message || 'Unable to update program support.');
        } finally { setSaving(false); }
    };

    const handleRemove = async () => {
        if (!programToRemove || saving || changed) return;
        setSaving(true);
        setMessage('');
        setError('');
        try {
            await axios.delete(`${APIURL}/programs`, {
                params: { program_id: programToRemove.program_id, company_id: props.comp_id, emp_id: props.user?.emp_id }
            });
            await loadProgramsData(false);
            setProgramToRemove(null);
            setMessage('Program enrollment removed.');
            notifyProgramChange();
        } catch (err) {
            setError(err.response?.data?.message || 'Unable to remove program enrollment.');
        } finally { setSaving(false); }
    };

    const handleDeleteDefinition = async () => {
        if (!programToDelete || saving || changed || isEnrolled(programToDelete)) return;
        setSaving(true);
        setMessage('');
        setError('');
        try {
            await axios.delete(`${APIURL}/programs/catalog/${programToDelete.medicaid_program_id}`, {
                params: { company_id: props.comp_id, emp_id: props.user?.emp_id }
            });
            setProgramToEnroll(current => current?.medicaid_program_id === programToDelete.medicaid_program_id ? null : current);
            await loadProgramsData(false);
            setProgramToDelete(null);
            setMessage('Custom program definition deleted.');
            notifyProgramChange();
        } catch (err) {
            setError(err.response?.data?.message || 'Unable to delete custom program definition.');
        } finally { setSaving(false); }
    };

    const optionPanel = (title, panelOptions, assigned) => (
        <Paper variant="outlined" sx={{ width: '100%' }}>
            <Typography variant="h6" align="center" sx={{ py: 1 }}>{title}</Typography>
            <Divider />
            <List dense role="list" sx={{ overflow: 'auto', height: 270 }}>
                {panelOptions.map(option => (
                    <ListItem key={option.id} role="listitem" disablePadding>
                        <ListItemButton disabled={!selectedProgram || saving} onClick={() => toggleOption(option)}>
                        <ListItemIcon>
                            <Checkbox checked={assigned} tabIndex={-1} disableRipple />
                        </ListItemIcon>
                        <ListItemText primary={option.label} />
                        </ListItemButton>
                    </ListItem>
                ))}
                {panelOptions.length === 0 && (
                    <ListItem>
                        <ListItemText
                            primary={!assigned && searchTerm ? 'No matching items' : 'No items'}
                            primaryTypographyProps={{ color: 'text.secondary', align: 'center' }}
                        />
                    </ListItem>
                )}
            </List>
        </Paper>
    );

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 160 }}>
                <CircularProgress size={48} />
            </Box>
        );
    }

    return (
        <Box sx={{ flexGrow: 1 }}>
            <Stack spacing={4}>
                <Paper sx={{ p: { xs: 3, sm: 4 } }}>
                    <Stack spacing={2}>
                        <Stack spacing={1} sx={{ mb: 1 }}>
                            <Typography variant="h5">Enroll in a Program</Typography>
                            <Typography variant="body1" color="text.secondary">
                                Choose a Platform-provided program for your agency's states or one of your existing custom programs
                            </Typography>
                        </Stack>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                            <Autocomplete
                                options={availablePrograms}
                                value={programToEnroll}
                                groupBy={program => program.company_id == null ? 'Platform' : 'Custom'}
                                getOptionLabel={program => [program.state_code, program.program_name].filter(Boolean).join(' - ')}
                                isOptionEqualToValue={(option, value) => option.medicaid_program_id === value.medicaid_program_id}
                                onChange={(event, value) => setProgramToEnroll(value)}
                                renderInput={params => <TextField {...params} label="Available Program" />}
                                disabled={saving || changed}
                                noOptionsText="No additional active programs available for your agency"
                                sx={{ flex: 1 }}
                            />
                            <Button variant="contained" onClick={handleEnroll} disabled={!programToEnroll || saving || changed}
                                sx={{ minWidth: 150, height: 56 }}>Enroll Program</Button>
                        </Stack>
                        {programs.length === 0 ? <Alert severity="info">Your agency has no enrolled programs yet.</Alert> : (
                            <Stack spacing={1}>
                                {programs.map(program => (
                                    <Box key={program.program_id} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
                                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="subtitle1" fontWeight={600}>{program.program_name}</Typography>
                                                <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                                                    <Chip size="small" label={program.definition_company_id == null ? 'Platform' : 'Custom'} />
                                                    {program.state_code && <Chip size="small" label={program.state_code} />}
                                                    {program.authority_type && <Chip size="small" variant="outlined" label={program.authority_type} />}
                                                    {!program.program_is_active && <Chip size="small" color="warning" label="Inactive" />}
                                                </Stack>
                                            </Box>
                                            <FormControlLabel label="Supported" control={
                                                <Switch checked={Boolean(program.is_supported)} disabled={saving || changed}
                                                    onChange={event => handleSupport(program, event.target.checked)}
                                                    slotProps={{ input: { 'aria-label': `Support ${program.program_name}` } }} />
                                            } />
                                            <Button onClick={() => {
                                                setSelectedProgram(program);
                                                manageProgramRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
                                            }} disabled={saving || changed}>Manage Assignments</Button>
                                            <Button color="error" onClick={() => setProgramToRemove(program)} disabled={saving || changed}>Remove Enrollment</Button>
                                        </Stack>
                                    </Box>
                                ))}
                            </Stack>
                        )}
                    </Stack>
                </Paper>



                <Paper ref={manageProgramRef} sx={{ p: { xs: 3, sm: 4 }, scrollMarginTop: '100px' }}>
                    <Stack spacing={2}>
                        <Stack spacing={1} sx={{ mb: 1 }}>
                            <Typography variant="h5">Manage a Program</Typography>
                            <Typography variant="body1" color="text.secondary">
                                Select an enrolled program to manage its assignments. Company programs can also be renamed.
                            </Typography>
                        </Stack>
                        <Divider />
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                            <Autocomplete
                                options={programs}
                                disabled={saving || changed}
                                value={selectedProgram}
                                getOptionLabel={program =>
                                    program.program_name || `Program #${program.program_id}`
                                }
                                isOptionEqualToValue={(option, value) =>
                                    String(option.program_id) === String(value.program_id)
                                }
                                onChange={(event, program) => setSelectedProgram(program)}
                                renderInput={(params) => <TextField {...params} label="Select Program" />}
                                sx={{ flex: 1 }}
                            />
                            <TextField
                                label="Program Name"
                                value={programName}
                                error={programNameEditError}
                                helperText={programNameEditError ? 'Program name is required' : selectedProgram && !canRename ? 'Platform program names are managed centrally' : ''}
                                inputProps={{ maxLength: 255 }}
                                onChange={(event) => {
                                    setProgramName(event.target.value);
                                    if (event.target.value.trim()) setProgramNameEditError(false);
                                }}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter') {
                                        event.preventDefault();
                                        handleRenameProgram();
                                    }
                                }}
                                disabled={!canRename || saving || changed}
                                sx={{ flex: 1 }}
                            />
                            <Button
                                variant="outlined"
                                onClick={handleRenameProgram}
                                disabled={
                                    !canRename || changed ||
                                    saving ||
                                    !programName.trim() ||
                                    programName.trim() === (selectedProgram?.program_name || '').trim()
                                }
                                sx={{ minWidth: 150, height: 56 }}
                            >
                                Rename Program
                            </Button>
                        </Stack>
                        <Typography variant="body1" align="center">
                            {selectedProgram
                                ? `Managing associations for ${selectedProgram.program_name || `Program #${selectedProgram.program_id}`}`
                                : 'Select a program to manage its associations'}
                        </Typography>

                        <Tabs
                            value={activeType}
                            onChange={(event, value) => {
                                setActiveType(value);
                                setSearchTerm('');
                            }}
                            variant="fullWidth"
                        >
                            {entityTypes.map(type => (
                                <Tab
                                    key={type.value}
                                    value={type.value}
                                    label={`${type.label} (${selectedByType[type.value].length})`}
                                    disabled={!selectedProgram}
                                />
                            ))}
                        </Tabs>

                        <TextField
                            fullWidth
                            label="Search Available Associations"
                            value={searchTerm}
                            onChange={(event) => setSearchTerm(event.target.value)}
                            disabled={!selectedProgram}
                        />

                        <Grid container spacing={2} justifyContent="center" alignItems="center">
                            <Grid size={{ xs: 12, md: 5 }}>
                                {optionPanel(`Available ${activeTypeDetails.label}`, availableOptions, false)}
                            </Grid>
                            <Grid size={{ xs: 12, md: 2 }}>
                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: { xs: 'row', md: 'column' },
                                    gap: 1,
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    <Button
                                        variant="outlined"
                                        onClick={() => updateSelected(current => [
                                            ...current,
                                            ...availableOptions.map(option => option.id)
                                        ])}
                                        disabled={!selectedProgram || saving || availableOptions.length === 0}
                                        aria-label={`Assign all visible ${activeTypeDetails.label}`}
                                    >
                                        {`Add all ${activeTypeDetails.label}`}
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        onClick={() => {
                                            const visibleIds = new Set(assignedOptions.map(option => option.id));
                                            updateSelected(current => current.filter(id => !visibleIds.has(id)));
                                        }}
                                        disabled={!selectedProgram || saving || assignedOptions.length === 0}
                                        aria-label={`Remove all visible ${activeTypeDetails.label}`}
                                    >
                                        {`Remove all ${activeTypeDetails.label}`}
                                    </Button>
                                </Box>
                            </Grid>
                            <Grid size={{ xs: 12, md: 5 }}>
                                {optionPanel(`Assigned ${activeTypeDetails.label}`, assignedOptions, true)}
                            </Grid>
                        </Grid>

                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, pt: 1 }}>
                            <Button disabled={!changed || saving} onClick={() => {
                                setSelectedByType(getSelections(associations, selectedProgram?.program_id));
                                setChanged(false);
                            }}>Discard Changes</Button>
                            <Button
                                variant="contained"
                                onClick={handleSaveAssociations}
                                disabled={!selectedProgram || !changed || saving}
                            >
                                {saving ? 'Saving...' : 'Save Associations'}
                            </Button>
                        </Box>
                    </Stack>
                </Paper>

                <Paper sx={{ p: { xs: 3, sm: 4 } }}>
                    <Stack spacing={2}>
                        <Stack spacing={1} sx={{ mb: 1 }}>
                            <Typography variant="h5">Create a Custom Program</Typography>
                            <Typography variant="body1" color="text.secondary">
                                Create a program owned by your agency. Your agency will be enrolled automatically.
                            </Typography>
                        </Stack>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
                            <TextField
                                fullWidth
                                required
                                label="New Program Name"
                                disabled={saving || changed}
                                value={newProgramName}
                                error={programNameError}
                                helperText={programNameError ? 'Program name is required' : ''}
                                inputProps={{ maxLength: 255 }}
                                onChange={(event) => {
                                    setNewProgramName(event.target.value);
                                    if (event.target.value.trim()) setProgramNameError(false);
                                }}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter') {
                                        event.preventDefault();
                                        handleCreateProgram();
                                    }
                                }}
                            />
                            <Button
                                variant="contained"
                                onClick={handleCreateProgram}
                                disabled={saving || changed}
                                sx={{ minWidth: 160, height: 56 }}
                            >
                                Create Program
                            </Button>
                        </Stack>
                        {customPrograms.length > 0 && (
                            <>
                                <Divider />
                                <Stack spacing={1}>
                                    <Typography variant="h6">Your Custom Programs</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        You must remove a program's enrollment before being able to delete its definition.
                                    </Typography>
                                    {customPrograms.map(program => (
                                        <Stack key={program.medicaid_program_id} direction={{ xs: 'column', sm: 'row' }}
                                            spacing={1} alignItems={{ sm: 'center' }}
                                            sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="subtitle1" fontWeight={600}>{program.program_name}</Typography>
                                                <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                                                    <Chip size="small" label={isEnrolled(program) ? 'Enrolled' : 'Not Enrolled'}
                                                        color={isEnrolled(program) ? 'primary' : 'default'} />
                                                    {!program.is_active && <Chip size="small" color="warning" label="Inactive" />}
                                                </Stack>
                                            </Box>
                                            <Button color="error" disabled={saving || changed || isEnrolled(program)}
                                                aria-label={`Delete custom program ${program.program_name}`}
                                                onClick={() => setProgramToDelete(program)}>Delete Custom Program</Button>
                                        </Stack>
                                    ))}
                                </Stack>
                            </>
                        )}
                    </Stack>
                </Paper>
            </Stack>
            <Dialog open={Boolean(programToRemove)} onClose={() => !saving && setProgramToRemove(null)}>
                <DialogTitle>Remove program enrollment?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Remove {programToRemove?.program_name} from your agency and clear its entity assignments?
                        The program definition will remain in the catalog. Programs assigned to projects must be reassigned first.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button disabled={saving} onClick={() => setProgramToRemove(null)}>Cancel</Button>
                    <Button disabled={saving} color="error" variant="contained" onClick={handleRemove}>
                        {saving ? 'Removing...' : 'Remove Enrollment'}
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={Boolean(programToDelete)} onClose={() => !saving && setProgramToDelete(null)}>
                <DialogTitle>Delete custom program definition?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Permanently delete {programToDelete?.program_name} from your company's program catalog?
                        This cannot be undone. You can keep the definition without an enrollment to reuse it later.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button disabled={saving} onClick={() => setProgramToDelete(null)}>Cancel</Button>
                    <Button disabled={saving} color="error" variant="contained" onClick={handleDeleteDefinition}>
                        {saving ? 'Deleting...' : 'Delete Custom Program'}
                    </Button>
                </DialogActions>
            </Dialog>
            <Snackbar
                open={Boolean(error || message)}
                autoHideDuration={4000}
                onClose={(event, reason) => {
                    if (reason === 'clickaway') return;
                    setMessage('');
                    setError('');
                }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    severity={error ? 'error' : 'success'}
                    variant="filled"
                    onClose={() => {
                        setMessage('');
                        setError('');
                    }}
                    sx={{ width: '100%' }}
                >
                    {error || message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
